import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { acceptInvite, getInvitePreview } from '@/lib/api/invite';
import { getProfile } from '@/lib/api/profile';
import { hasCreatedProfile } from '@/lib/profileStatus';
import { ApiError } from '@/lib/api/core';
import { showSuccessToast } from '@/lib/toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/supabaseClient';
import { useTranslation } from 'react-i18next';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string };

export function InviteAcceptPage() {
  const { t } = useTranslation('player');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    if (!token || authLoading) return;
    let cancelled = false;

    (async () => {
      // Not signed in? Send them to login, preserving the invite token.
      // The auth context may lag behind Supabase's own session store for a
      // tick after signInWithPassword — double-check via getSession before
      // redirecting, to avoid a login ↔ invite bounce after post-login nav.
      let signedIn = !!user;
      if (!signedIn) {
        const { data } = await supabase.auth.getSession();
        signedIn = !!data.session;
      }
      if (!signedIn) {
        navigate(`/login?invite=${encodeURIComponent(token)}`, { replace: true });
        return;
      }

      try {
        // Preview first — this surfaces expired / already-accepted /
        // not-found as a friendly GoneException before we try to attach.
        const preview = await getInvitePreview(token);
        if (cancelled) return;

        // Signed in but no profile? Funnel through profile creation.
        const profile = await getProfile().catch(() => null);
        if (cancelled) return;
        if (!hasCreatedProfile(profile)) {
          navigate(`/profile?invite=${encodeURIComponent(token)}`, { replace: true });
          return;
        }

        // One-click accept: the click on the email link is the consent.
        await acceptInvite(token);
        if (cancelled) return;
        showSuccessToast(
          t('invite_accept.success', { tournamentName: preview.tournamentName }),
        );
        navigate('/player/dashboard', { replace: true });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.userMessage
            : t('invite_accept.default_error');
        setPhase({ kind: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, user, authLoading, navigate]);

  return (
    <div
      data-testid="invite-accept-page"
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
            <Crown className="h-6 w-6 text-accent-foreground" />
          </div>
          <CardTitle className="text-2xl">{t('invite_accept.card_title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {phase.kind === 'loading' && (
            <p className="text-center text-muted-foreground">{t('invite_accept.attaching')}</p>
          )}

          {phase.kind === 'error' && (
            <p
              data-testid="invite-error-message"
              className="text-center text-sm text-destructive"
            >
              {phase.message}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
